import type { IMilestone } from '@pulse/shared';

import { apiFetch } from '../../../lib/api-client';
import { extractErrorMessage } from '../../../lib/api-error.util';

const base = (projectId: string) => `/api/v1/projects/${projectId}/milestones`;

export interface CreateMilestoneInput {
  type: string;
  name: string;
  // `null` clears Description (the backend column is nullable); `undefined`/omitted leaves it
  // unchanged on update. notificationEmail has no clear semantics yet — its DB column is
  // NOT NULL — so it stays a plain optional string, not nullable.
  description?: string | null;
  notificationEmail?: string;
}
export type UpdateMilestoneInput = Partial<CreateMilestoneInput>;

export async function fetchMilestones(projectId: string): Promise<IMilestone[]> {
  const res = await apiFetch(base(projectId));
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch milestones'));
  return res.json() as Promise<IMilestone[]>;
}

export async function createMilestone(projectId: string, data: CreateMilestoneInput): Promise<IMilestone> {
  const res = await apiFetch(base(projectId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to create milestone'));
  return res.json() as Promise<IMilestone>;
}

export async function updateMilestone(projectId: string, milestoneId: string, data: UpdateMilestoneInput): Promise<IMilestone> {
  const res = await apiFetch(`${base(projectId)}/${milestoneId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update milestone'));
  return res.json() as Promise<IMilestone>;
}

// §15 — deactivating a milestone with associated issues returns 409; the caller distinguishes
// this from other failures to offer Cancel / "dissociate and deactivate" instead of just
// showing a generic error.
export class MilestoneAssociatedIssuesError extends Error {}

export async function deactivateMilestone(projectId: string, milestoneId: string, dissociateIssues: boolean): Promise<IMilestone> {
  const res = await apiFetch(`${base(projectId)}/${milestoneId}/deactivate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ dissociateIssues }),
  });
  if (!res.ok) {
    const message = await extractErrorMessage(res, 'Failed to deactivate milestone');
    throw res.status === 409 ? new MilestoneAssociatedIssuesError(message) : new Error(message);
  }
  return res.json() as Promise<IMilestone>;
}

export async function activateMilestone(projectId: string, milestoneId: string): Promise<IMilestone> {
  const res = await apiFetch(`${base(projectId)}/${milestoneId}/activate`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to activate milestone'));
  return res.json() as Promise<IMilestone>;
}
